import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import AppIcon from '../../../../components/AppIcon';
import defaultProfile from '../../../../assets/images/defaultProfile.png';
import { getFullUrl } from '../../../../utils/url';
import { appDate } from '../../../../utils/appDateFormat';
import { parseApiUtc } from '@/utils/apiDate';

interface Resource {
  title: string;
  url: string;
}

interface ManagerResponse {
  id: string;
  message: string;
  managerName: string;
  managerAvatar: string;
  createdAt: string;
  isRead: boolean;
  encouragementType: string;
  actionItems: string[];
  resources: Resource[];
}

interface ManagerResponseCardProps {
  response: ManagerResponse | null;
}

const getAvatarUrl = (avatarUrl?: string): string => {
  return getFullUrl(avatarUrl) || defaultProfile;
};

const ManagerResponseCard: React.FC<ManagerResponseCardProps> = ({ response }) => {
  const { t, i18n } = useTranslation();

  if (!response) {
    return (
      <Card>
        <CardContent>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('performance.reflections.managerFeedback')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('performance.reflections.noFeedbackYet')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={!response.isRead ? 'border-primary shadow-md' : ''}>
      <CardContent>
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t('performance.reflections.managerFeedback')}
          </h3>
          {!response.isRead && (
            <StatusBadge variant="info" icon={Bell}>
              {t('performance.reflections.newFeedback')}
            </StatusBadge>
          )}
        </div>

        <div className="flex items-start gap-4 mb-6">
          <img
            src={getAvatarUrl(response.managerAvatar)}
            alt={response.managerName}
            className="w-14 h-14 rounded-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).src = defaultProfile; }}
          />
          <div className="flex-1">
            <p className="text-base font-bold text-foreground">{response.managerName}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <AppIcon name="Clock" size={14} />
              {appDate(parseApiUtc(response.createdAt))}
            </p>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg border border-border mb-6">
          <p className="text-sm text-foreground">{response.message}</p>
        </div>

        {response.actionItems?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-base font-bold text-foreground mb-2 flex items-center gap-2">
              <AppIcon name="ClipboardList" size={18} className="text-primary" />
              {t('performance.reflections.actionItems')}
            </h4>
            <ul className="space-y-2">
              {response.actionItems.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                  <AppIcon name="ClipboardList" size={14} className="text-primary mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {response.resources?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-base font-bold text-foreground mb-2 flex items-center gap-2">
              <AppIcon name="Link" size={18} className="text-primary" />
              {t('performance.reflections.recommendedResources')}
            </h4>
            <ul className="space-y-2">
              {response.resources.map((resource, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <AppIcon name="Link" size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {resource.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ManagerResponseCard;
